/**
 * Provider reassignment for the happy-flow mark-as-completed modal.
 * @module headerModals/utils/runHappyFlowProviderReassignment
 */

import { OrderReassignmentPayload } from "../../../../../components/modals/reAssignModal/types";
import { insertReassignTrailComments } from "../../../../../components/modals/reAssignModal/utils/insertReassignTrailComments";
import { getAssigneeDisplayName } from "../../../../../utils/orderTrailComment";
import { resolveLinkedOrderUpdateOrderIds } from "../../../../../utils/resolveLinkedOrderUpdateOrderIds";
import { RunHappyFlowProviderReassignmentParams } from "../types";

/**
 * Persists happy-flow provider reassignment via single-order or bulk Postgres
 * update, then inserts trail comments for every updated order.
 *
 * @param params - Assignment runners, order context, and acting provider metadata
 */
export const runHappyFlowProviderReassignment = async (
  params: RunHappyFlowProviderReassignmentParams,
): Promise<void> => {
  const {
    parentOrderId,
    selectedOrderIds,
    selectedProvider,
    generalOrderDetails,
    orgId,
    userEmail,
    actingProviderName,
    runBatchAssignmentUpdate,
    runUpdateGeneralOrderDetails,
  } = params;

  const hasSelectedSiblingOrders =
    selectedOrderIds != null && selectedOrderIds.length > 0;
  const assignmentPayload: OrderReassignmentPayload = {
    assigned_to_id: selectedProvider.id,
    assigned_to_name: selectedProvider.name,
  };

  const assignmentResult = hasSelectedSiblingOrders
    ? await runBatchAssignmentUpdate(
        resolveLinkedOrderUpdateOrderIds(parentOrderId, selectedOrderIds),
        assignmentPayload,
      )
    : await runUpdateGeneralOrderDetails(assignmentPayload);

  if (!assignmentResult.success) {
    return;
  }

  const fromAssignee = getAssigneeDisplayName(
    generalOrderDetails?.assigned_to?.assigned_to_id,
    generalOrderDetails?.assigned_to?.assigned_to_name,
  );
  const patientId =
    generalOrderDetails?.demographics?.patient_id ??
    generalOrderDetails?.demographics?.patient_mrn;
  const orderIdsForTrail = hasSelectedSiblingOrders
    ? resolveLinkedOrderUpdateOrderIds(parentOrderId, selectedOrderIds)
    : [parentOrderId];

  insertReassignTrailComments({
    orderIds: orderIdsForTrail,
    orgId,
    patientId,
    providerEmail: userEmail,
    providerName: actingProviderName,
    fromAssignee,
    toAssignee: selectedProvider.name,
  });
};
